import { Box } from "@mui/material";
import { PageHeader } from "../../page-header";
import { SubHeader } from "../../sub-header";
import styles from "./customer-contract-notes.module.scss";
import { useContext, useEffect, useState } from "react";
import {
  PrimaryButton,
  RiverSpinner,
  RiverTextInput,
  useNotification,
  useSimpleDialog,
} from "@river/common-ui";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Close";
import { CustomerContractNotesAddEditDialog } from "./customer-contract-notes-add-edit-dialog";
import { IContractNote } from "@river/interfaces";
import { customerService } from "../../../services";
import { CustomerContext } from "../../customer/customer.context";
import { ContractContext } from "../contract.context";
import dayjs from "dayjs";

export const CustomerContractNotes = () => {
  const notify = useNotification();
  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const [noteToEdit, setNoteToEdit] = useState<IContractNote | null>(null);
  const [noteToDelete, setNoteToDelete] = useState<IContractNote | null>(null);
  const [notes, setNotes] = useState<IContractNote[]>([]);
  const [loadingContractNotes, setLoadingContractNotes] =
    useState<boolean>(false);
  const { customer } = useContext(CustomerContext)!;
  const { contract, fetchContract } = useContext(ContractContext)!;

  async function fetchNotes() {
    if (customer?._id && contract) {
      setLoadingContractNotes(true);
      try {
        const data = await customerService.getContractNotes(
          customer._id,
          contract._id
        );
        setNotes(data);
      } catch (message) {
        notify.error({ message });
      } finally {
        setLoadingContractNotes(false);
      }
    }
  }

  async function deleteNote() {
    try {
      if (customer?._id && contract?._id && noteToDelete?._id) {
        await customerService.deleteContractNote(
          customer._id,
          contract._id,
          noteToDelete!._id!
        );
        setNoteToDelete(null);
        fetchNotes();
      }
    } catch (message) {
      notify.error({ message });
    }
  }

  const deleteNoteConfirmationDialog = useSimpleDialog({
    title: "Delete Note",
    message: "Are you sure you want to delete this note?",
    confirmButtonText: "Delete",
    onConfirm: deleteNote,
    icon: DeleteIcon,
  });

  useEffect(() => {
    if (noteToDelete) {
      deleteNoteConfirmationDialog.open();
    }
  }, [noteToDelete]);

  useEffect(() => {
    fetchNotes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contract]);

  useEffect(() => {
    if (!contract) {
      fetchContract();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <PageHeader title="Contract" />
      <SubHeader subtitle="Notes" />
      <Box className={styles.container}>
        <Box className={styles.btnContainer}>
          <PrimaryButton
            text="Add Note"
            onClick={() => setOpenDialog(true)}
            disabled={!contract}
          />
        </Box>
        <Box className={styles.list}>
          {!loadingContractNotes &&
            notes.map((note) => (
              <Box key={note._id} className={styles.noteContainer}>
                <Box className={styles.note}>
                  <RiverTextInput
                    id={`note-${note._id}`}
                    value={note.note || ""}
                    inputProps={{ multiline: true, readOnly: true, maxRows: 4 }}
                    fullWidth
                  />
                  <span className={styles.createdAt}>
                    {dayjs(note.created_at).format("YYYY-MM-DD HH:mm:ss")}
                  </span>
                </Box>
                <Box className={styles.actions}>
                  <EditIcon
                    onClick={() => {
                      setNoteToEdit(note);
                      setOpenDialog(true);
                    }}
                    className={styles.icon}
                    titleAccess="Edit Note"
                  />
                  <DeleteIcon
                    color="error"
                    className={styles.icon}
                    titleAccess="Delete Note"
                    onClick={() => {
                      setNoteToDelete(note);
                    }}
                  />
                </Box>
              </Box>
            ))}
        </Box>
        <RiverSpinner show={loadingContractNotes} />
      </Box>

      {openDialog && (
        <CustomerContractNotesAddEditDialog
          open={openDialog}
          noteToEdit={noteToEdit}
          onClose={async (success) => {
            setOpenDialog(false);
            if (success) {
              await fetchNotes();
            }
            setNoteToEdit(null);
          }}
        />
      )}

      {deleteNoteConfirmationDialog.render()}
    </>
  );
};
